import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import FilterModal from "../Filter Modal/FilterModal";
import axios from "axios";
import Swal from "sweetalert2";

export default function DataTable_Component({ columns, viewSalaryhistory }) {
  const [apidata, setApidata] = useState([]);
  const [dataFilter, setDataFilter] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [clientFilter, setClientFilter] = useState([]);

  const [statusFilter, setStatusFilter] = useState("");
  const [userSourceFilter, setUserSourceFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState([]);
  const [staffTypeFilter, setStaffTypeFilter] = useState([]);
  const [isFinalizedFilter, setIsFinalizedFilter] = useState("");
  const [isPaidFilter, setIsPaidFilter] = useState("");
  const [pendingEdits, setPendingEdits] = useState({});
  const [activeCell, setActiveCell] = useState(null);
  const [savingChanges, setSavingChanges] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const editableFields = [
    "advanceMoney",
    "vehicleDamagePenalty",
    "cashSort",
    "routeabsentism",
    "recoverablecod",
    "philfridge",
    "challanAmount",
    "actual_salary",
    "actual_pay",
    "totalOrders",
  ];

  const handleBulkUpdate = async (field) => {
    if (selectedRows.length === 0) {
      Swal.fire(
        "Select Employees",
        "Please select at least one employee.",
        "warning",
      );
      return;
    }

    const isFinalizing = field === "isFinalized";

    // Prevent marking unfinalized salaries as paid in the UI too
    if (field === "isPaid" && selectedRows.some((row) => !row.isFinalized)) {
      Swal.fire(
        "Cannot Mark as Paid",
        "All selected salaries must be finalized first. Please finalize them or select only finalized salaries.",
        "warning",
      );
      return;
    }

    const actionLabel = isFinalizing ? "finalize" : "mark as paid";

    const confirmation = await Swal.fire({
      icon: "question",
      title: "Confirm Bulk Update",
      text: `Are you sure you want to ${actionLabel} ${selectedRows.length} selected salary record(s)?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Continue",
      cancelButtonText: "Cancel",
    });

    if (!confirmation.isConfirmed) return;

    const token = localStorage.getItem("token");

    if (!token) {
      Swal.fire("Unauthorized", "Authentication token not found.", "error");
      return;
    }

    try {
      setBulkUpdating(true);

      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/api/v4/salary-history/salary-history/bulk-update`,
        {
          ids: selectedRows.map((row) => row._id),
          field,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Bulk update failed.");
      }

      await fetchSalaryData(year, month);

      setSelectedRows([]);
      setClearSelectedRows((prev) => !prev);

      Swal.fire({
        icon: "success",
        title: "Updated Successfully",
        text: response.data.message,
      });
    } catch (error) {
      console.error("Bulk salary update failed:", error);

      Swal.fire(
        "Update Failed",
        error.response?.data?.message ||
          error.message ||
          "Unable to update the selected salary records.",
        "error",
      );
    } finally {
      setBulkUpdating(false);
    }
  };

  const editKey = (rowId, field) => `${rowId}:${field}`;

  const getCellValue = (row, field) => {
    const key = editKey(row._id, field);

    return Object.prototype.hasOwnProperty.call(pendingEdits, key)
      ? pendingEdits[key]
      : (row[field] ?? 0);
  };

  const handleCellChange = (row, field, value) => {
    setPendingEdits((prev) => ({
      ...prev,
      [editKey(row._id, field)]: value,
    }));
  };

  const renderEditableCell = (row, field) => {
    const key = editKey(row._id, field);

    const isActive =
      activeCell?.rowId === row._id && activeCell?.field === field;

    const isEdited = Object.prototype.hasOwnProperty.call(pendingEdits, key);

    const value = getCellValue(row, field);

    return (
      <div
        className={`salary-edit-cell ${
          isActive ? "active" : ""
        } ${isEdited ? "pending" : ""}`}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => {
          e.stopPropagation();

          if (!isActive) {
            setActiveCell({ rowId: row._id, field });
          }
        }}
        title={isActive ? "" : "Double-click to edit"}
      >
        {isActive ? (
          <input
            autoFocus
            type="number"
            min="0"
            step={field === "totalOrders" ? "1" : "0.01"}
            value={value}
            onChange={(e) => handleCellChange(row, field, e.target.value)}
            onBlur={() => setActiveCell(null)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }

              if (e.key === "Escape") {
                setPendingEdits((prev) => {
                  const next = { ...prev };
                  delete next[key];
                  return next;
                });

                setActiveCell(null);
              }
            }}
          />
        ) : (
          <span>
            {" "}
            {field === "totalOrders"
              ? Number(value || 0)
              : Number(value || 0).toFixed(2)}
          </span>
        )}
      </div>
    );
  };

  const editableColumns = columns.map((column) => {
    if (!editableFields.includes(column.id)) {
      return column;
    }

    return {
      ...column,
      minWidth: column.minWidth || "115px",

      cell: (row) => renderEditableCell(row, column.id),
      ignoreRowClick: true,
    };
  });

  const uniqueProviders = [
    ...new Set(
      apidata
        .map((item) => item?.id_user?.thirdPartyProvider)
        .filter(
          (provider) =>
            typeof provider === "string" && provider.trim().length > 0,
        )
        .map((provider) => provider.trim()),
    ),
  ].sort((a, b) => a.localeCompare(b));

  const uniqueStaffTypes = [
    ...new Set(
      apidata
        .map((item) => item?.id_user?.id_generalStaffType?.name)
        .filter(
          (staffType) =>
            typeof staffType === "string" && staffType.trim().length > 0,
        )
        .map((staffType) => staffType.trim()),
    ),
  ].sort((a, b) => a.localeCompare(b));

  const fetchSalaryData = async (yearParam, monthParam) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return console.error("No token found");

      const fetchSalary = async (year, month) => {
        return axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/v4/salary-history/get-salary-history?year=${year}&month=${month}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      };

      // Step 1: Default to previous month
      let yearVal = yearParam;
      let monthVal = monthParam;

      if (!yearVal || !monthVal) {
        const today = new Date();
        const prevMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1,
        );
        yearVal = prevMonth.getFullYear();
        monthVal = prevMonth.getMonth() + 1;
      }

      // Step 2: Try initial fetch
      let response = await fetchSalary(yearVal, monthVal);

      if (
        response.data.success &&
        Array.isArray(response.data.message) &&
        response.data.message.length > 0
      ) {
        const result = response.data.message;
        // setApidata(result);
        // setFilteredData(result);
        setApidata(result);
        setFilteredData(result);

        // auto-fill filter inputs based on API data
        if (result.length > 0) {
          setMonth(result[0].month);
          setYear(result[0].year);
        }
        return;
      }

      // Step 3: Find latest available month
      const today = new Date();

      for (let i = 0; i < 12; i++) {
        const tryDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const tryYear = tryDate.getFullYear();
        const tryMonth = tryDate.getMonth() + 1;

        if (tryYear === yearVal && tryMonth === monthVal) continue;

        const tryResponse = await fetchSalary(tryYear, tryMonth);

        if (
          tryResponse.data.success &&
          Array.isArray(tryResponse.data.message) &&
          tryResponse.data.message.length > 0
        ) {
          const result = tryResponse.data.message;

          setApidata(result);
          setFilteredData(result);
          setMonth(String(tryMonth));
          setYear(String(tryYear));

          console.log(
            `No data for ${monthVal}/${yearVal}. Showing ${tryMonth}/${tryYear}`,
          );

          return;
        }
      }

      console.warn("No salary data available for recent months");
    } catch (error) {
      console.error("Error fetching salary data:", error);
    }
  };

  const generateSalary = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return console.error("No token found");

      if (!year || !month) {
        Swal.fire({
          icon: "warning",
          title: "Missing Inputs",
          text: "Please select both year and month before generating salary.",
        });
        return;
      }

      const confirm = await Swal.fire({
        icon: "question",
        title: "Generate Salary?",
        text: `Generate salary for ${month}/${year}?`,
        showCancelButton: true,
        confirmButtonText: "Generate",
      });

      if (!confirm.isConfirmed) return;

      Swal.fire({
        title: "Generating Salary...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/v4/salary-history/generate`,
        { year, month },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      Swal.close();

      if (response.data.success) {
        await Swal.fire({
          icon: "success",
          title: "Salary Generated",
          text: "Salary generated successfully.",
        });

        // Refresh table
        await fetchSalaryData(year, month);

        setShowFilterModal(false);
      } else {
        Swal.fire({
          icon: "error",
          title: "Generation Failed",
          text: response.data.message || "Something went wrong.",
        });
      }
    } catch (error) {
      Swal.close();
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to generate salary.",
      });
    }
  };

  const applyFilter = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return console.error("No token found");

      if (!year || !month) {
        Swal.fire({
          icon: "warning",
          title: "Missing Inputs",
          text: "Please select both year and month before applying filters.",
        });
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v4/salary-history/get-salary-history?year=${year}&month=${month}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = response.data.message;

      if (response.data.success && Array.isArray(result) && result.length > 0) {
        setApidata(result);
        setFilteredData(result);
        setShowFilterModal(false);
        return;
      }

      const generateConfirm = await Swal.fire({
        icon: "info",
        title: "No Data Found",
        text: `Salary data not found for ${month}/${year}. Do you want to generate it now?`,
        showCancelButton: true,
        confirmButtonText: "Generate Salary",
        cancelButtonText: "Cancel",
      });

      if (generateConfirm.isConfirmed) {
        // 🔄 Show loading
        Swal.fire({
          title: "Generating Salary...",
          html: "Please wait while we process the salary.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const generateRes = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/api/v4/salary-history/generate`,
          { year, month },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        Swal.close();

        if (generateRes.data.success) {
          await Swal.fire({
            icon: "success",
            title: "Salary Generated",
            text: "Salary has been generated successfully!",
            confirmButtonText: "OK",
          });

          // ✅ Refresh the table
          await fetchSalaryData(year, month);
          setShowFilterModal(false);
        } else {
          Swal.fire({
            icon: "error",
            title: "Generation Failed",
            text: generateRes.data.message || "Something went wrong.",
          });
        }
      }
    } catch (error) {
      Swal.close();
      console.error("Error during applyFilter:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong while applying filters.",
      });
    }
  };

  const removeFilter = async () => {
    // Reset every filter
    setClientFilter([]);
    setStatusFilter("");
    setUserSourceFilter("");
    setProviderFilter([]);
    setStaffTypeFilter([]);
    setIsFinalizedFilter("");
    setIsPaidFilter("");
    setDataFilter("");

    // Clear the selected period; fetchSalaryData will use
    // the previous month as its default.
    setMonth("");
    setYear("");

    setShowFilterModal(false);

    // Reload the default salary data
    await fetchSalaryData();
  };

  useEffect(() => {
    fetchSalaryData();
  }, []);

  const handleSearch = () => {
    if (!dataFilter) return apidata;
    return apidata.filter((item) =>
      item.id_user?.name?.toLowerCase().startsWith(dataFilter.toLowerCase()),
    );
  };

  const getFilteredData = () => {
    let filtered = apidata;

    // 1. Search by employee name
    // Search by employee name, phone number, or employee ID
    if (dataFilter.trim()) {
      const searchTerm = dataFilter.trim().toLowerCase();

      filtered = filtered.filter((item) => {
        const employeeName = String(item.id_user?.name || "").toLowerCase();
        const phoneNumber = String(
          item.id_user?.phonenumber || "",
        ).toLowerCase();
        const employeeId = String(item.id_employee || "").toLowerCase();

        return (
          employeeName.includes(searchTerm) ||
          phoneNumber.includes(searchTerm) ||
          employeeId.includes(searchTerm)
        );
      });
    }

    // 2. Client filter
    if (clientFilter.length > 0) {
      filtered = filtered.filter((item) => {
        const clients = item.id_user?.id_client;

        if (!Array.isArray(clients)) return false;

        return clients.some((client) => clientFilter.includes(client.name));
      });
    }

    // 3. User status: Active / Inactive
    if (statusFilter) {
      filtered = filtered.filter(
        (item) => item.id_user?.status === statusFilter,
      );
    }

    // 4. User source: InHouse / ThirdParty
    if (userSourceFilter) {
      filtered = filtered.filter(
        (item) => item.id_user?.userSource === userSourceFilter,
      );
    }

    // 5. Third-party provider
    if (providerFilter.length > 0) {
      filtered = filtered.filter((item) =>
        providerFilter.includes(item.id_user?.thirdPartyProvider?.trim()),
      );
    }

    // 6. General staff type
    if (staffTypeFilter.length > 0) {
      filtered = filtered.filter((item) => {
        const staffType = item.id_user?.id_generalStaffType?.name;
        return staffType && staffTypeFilter.includes(staffType.trim());
      });
    }

    // 7. Salary finalized status
    if (isFinalizedFilter !== "") {
      filtered = filtered.filter(
        (item) => item.isFinalized === (isFinalizedFilter === "true"),
      );
    }

    // 8. Salary paid status
    if (isPaidFilter !== "") {
      filtered = filtered.filter(
        (item) => item.isPaid === (isPaidFilter === "true"),
      );
    }

    return filtered;
  };

  const displayedData = getFilteredData();

  // const totalTrips = displayedData.reduce(
  //   (sum, item) => sum + (Number(item.totalOrders) || 0),
  //   0,
  // );

  const totalCount = displayedData.length;

  const totalFinalSalary = displayedData.reduce(
    (sum, item) => sum + (Number(item.actual_salary) || 0),
    0,
  );

  const cancelChanges = () => {
    setPendingEdits({});
    setActiveCell(null);
  };

  const saveChanges = async () => {
    if (Object.keys(pendingEdits).length === 0) return;

    const token = localStorage.getItem("token");
    if (!token) {
      Swal.fire("Error", "Authentication token not found.", "error");
      return;
    }

    // Group all changed fields by salary-history record.
    const updatesByRow = {};

    for (const [key, rawValue] of Object.entries(pendingEdits)) {
      const separatorIndex = key.lastIndexOf(":");
      const rowId = key.slice(0, separatorIndex);
      const field = key.slice(separatorIndex + 1);
      const value = Number(rawValue);

      if (
        !editableFields.includes(field) ||
        rawValue === "" ||
        !Number.isFinite(value) ||
        value < 0 ||
        (field === "totalOrders" && !Number.isInteger(value))
      ) {
        Swal.fire(
          "Invalid value",
          field === "totalOrders"
            ? "Total Trips must be a non-negative whole number."
            : "Enter a valid non-negative number for every edited field.",
          "warning",
        );
        return;
      }
      if (!updatesByRow[rowId]) {
        updatesByRow[rowId] = {};
      }

      updatesByRow[rowId][field] = value;
    }

    try {
      setSavingChanges(true);

      for (const [rowId, updates] of Object.entries(updatesByRow)) {
        await axios.patch(
          `${process.env.REACT_APP_BASE_URL}/api/v4/salary-history/salary-history/${rowId}`,
          updates,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
      }

      setPendingEdits({});
      setActiveCell(null);

      await fetchSalaryData(year, month);
      Swal.fire(
        "Saved",
        "Salary and deduction changes have been saved successfully.",
        "success",
      );
    } catch (error) {
      console.error("Failed to save salary deductions:", error);

      Swal.fire(
        "Save failed",
        "Some changes may have been saved. Refresh the table and verify the values before retrying.",
        "error",
      );

      await fetchSalaryData(year, month);
    } finally {
      setSavingChanges(false);
    }
  };

  return (
    <div className="table-responsive">
      <div className="row justify-content-between mb-2">
        <div className="col-md-3 mb-2">
          <input
            type="text"
            placeholder="Search name, phone or employee ID"
            className="form-control"
            value={dataFilter}
            onChange={(e) => setDataFilter(e.target.value)}
          />
        </div>

        <div className="col-lg-9 col-md-8">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <button
              className="btn btn-primary"
              onClick={() => setShowFilterModal(true)}
            >
              <i className="fa fa-filter me-2" />
              Filter
            </button>

            <div className="d-flex align-items-center flex-wrap gap-2 ms-2">
              <div
                className="border rounded px-3 py-2"
                style={{ whiteSpace: "nowrap", backgroundColor: "#f8f9fa" }}
              >
                <span className="text-muted">Total Count : </span>
                <strong className="text-primary">
                  {totalCount.toLocaleString("en-IN")}
                </strong>
              </div>

              <div
                className="border rounded px-3 py-2"
                style={{ whiteSpace: "nowrap", backgroundColor: "#f8f9fa" }}
              >
                <span className="text-muted">Total Final Salary : </span>
                <strong className="text-success">
                  ₹
                  {totalFinalSalary.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </strong>
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectedRows.length > 0 && (
        <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
          <span className="fw-semibold me-2">
            {selectedRows.length} employee(s) selected
          </span>

          <button
            type="button"
            className="btn btn-success"
            onClick={() => handleBulkUpdate("isFinalized")}
            disabled={bulkUpdating}
          >
            {bulkUpdating ? "Updating..." : "Finalize Selected"}
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleBulkUpdate("isPaid")}
            disabled={
              bulkUpdating || selectedRows.some((row) => !row.isFinalized)
            }
            title={
              selectedRows.some((row) => !row.isFinalized)
                ? "All selected salaries must be finalized first"
                : "Mark selected salaries as paid"
            }
          >
            {bulkUpdating ? "Updating..." : "Mark Selected as Paid"}
          </button>
        </div>
      )}
      {displayedData.length > 0 ? (
        <DataTable
          // columns={columns}
          columns={editableColumns}
          data={displayedData}
          pagination
          fixedHeader
          highlightOnHover
          responsive
          paginationPerPage={15}
          paginationRowsPerPageOptions={[15, 25, 50, 100]}
          selectableRows
          selectableRowsHighlight
          onSelectedRowsChange={({ selectedRows }) => {
            setSelectedRows(selectedRows);
          }}
          clearSelectedRows={clearSelectedRows}
        />
      ) : (
        <p>No results found.</p>
      )}

      {Object.keys(pendingEdits).length > 0 && (
        <div className="salary-edit-footer">
          <div className="salary-pending-message">
            <span className="salary-pending-count">
              {Object.keys(pendingEdits).length}
            </span>

            <div>
              <div>Unsaved changes</div>
              <div className="salary-pending-description">
                Review your edits before saving.
              </div>
            </div>
          </div>

          <div className="salary-edit-footer-actions">
            <button
              type="button"
              className="btn salary-cancel-btn"
              onClick={cancelChanges}
              disabled={savingChanges}
            >
              Cancel
            </button>

            <button
              type="button"
              className="btn salary-save-btn"
              onClick={saveChanges}
              disabled={savingChanges}
            >
              {savingChanges ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}

      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        applyFilter={applyFilter}
        removeFilter={removeFilter}
        generateSalary={generateSalary}
        month={month}
        setMonth={setMonth}
        year={year}
        setYear={setYear}
        clientFilter={clientFilter}
        setClientFilter={setClientFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        userSourceFilter={userSourceFilter}
        setUserSourceFilter={setUserSourceFilter}
        providerFilter={providerFilter}
        setProviderFilter={setProviderFilter}
        staffTypeFilter={staffTypeFilter}
        setStaffTypeFilter={setStaffTypeFilter}
        isFinalizedFilter={isFinalizedFilter}
        setIsFinalizedFilter={setIsFinalizedFilter}
        isPaidFilter={isPaidFilter}
        setIsPaidFilter={setIsPaidFilter}
        uniqueProviders={uniqueProviders}
        uniqueStaffTypes={uniqueStaffTypes}
        apidata={apidata}
      />
    </div>
  );
}
